import { createContext, useCallback, useContext, useEffect, useState } from 'react';

const STORAGE_KEY = 'agn_theme';
const ThemeContext = createContext({ theme: 'dark', toggle: () => {}, setTheme: () => {} });

/**
 * ThemeProvider — gestisce il tema chiaro/scuro persistendolo in localStorage.
 * Default: 'dark' (obsidian). L'utente puo' passare al tema chiaro dal menu Dashboard.
 * La classe `light` viene applicata su `document.documentElement` per cascading CSS.
 */
export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === 'light' || saved === 'dark') return saved;
    } catch {}
    return 'dark';
  });

  const applyTheme = useCallback((t) => {
    const root = document.documentElement;
    if (t === 'light') {
      root.classList.add('light');
      root.classList.remove('dark');
    } else {
      root.classList.add('dark');
      root.classList.remove('light');
    }
    // Aggiorna anche il meta theme-color per PWA/mobile browser chrome
    try {
      const meta = document.querySelector('meta[name="theme-color"]');
      if (meta) meta.setAttribute('content', t === 'light' ? '#F4F3F0' : '#080A09');
    } catch {}
  }, []);

  useEffect(() => {
    applyTheme(theme);
    try { localStorage.setItem(STORAGE_KEY, theme); } catch {}
  }, [theme, applyTheme]);

  const setTheme = useCallback((t) => setThemeState(t === 'light' ? 'light' : 'dark'), []);
  const toggle = useCallback(() => setThemeState((p) => (p === 'light' ? 'dark' : 'light')), []);

  return (
    <ThemeContext.Provider value={{ theme, toggle, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
